import { memo, useState } from "react";
import type { EventId, ThreadId } from "@t3tools/contracts";
import { useQuery } from "@tanstack/react-query";
import { activityDetailQueryOptions } from "../../lib/threadDataReactQuery";
import { deriveWorkLogEntries } from "../../session-logic";
import {
  BotIcon,
  BookOpenIcon,
  CheckIcon,
  ChevronDownIcon,
  CircleAlertIcon,
  EyeIcon,
  FolderIcon,
  GlobeIcon,
  HammerIcon,
  type LucideIcon,
  SearchIcon,
  SquarePenIcon,
  TerminalIcon,
  WrenchIcon,
  ZapIcon,
} from "lucide-react";
import type { WorkLogEntry } from "../../session-logic";
import { cn } from "../../lib/utils";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "../ui/collapsible";
import { TextOutput } from "../TextOutput";
import { normalizeCompactToolLabel } from "./MessagesTimeline.logic";
import {
  commandActionLabel,
  dynamicToolLabel,
  workEntryCategory,
  workGroupLabel,
} from "./ActivityGroup.logic";

export const ActivityGroup = memo(function ActivityGroup({
  threadId,
  entries,
  open,
  onOpenChange,
}: {
  threadId?: ThreadId | undefined;
  entries: readonly WorkLogEntry[];
  open: boolean;
  onOpenChange: () => void;
}) {
  const GroupIcon = entries[0] ? workEntryIcon(entries[0]) : ZapIcon;
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="min-w-0">
      <CollapsibleTrigger className="flex max-w-full items-center gap-2 rounded py-1 text-left text-sm leading-5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
        <GroupIcon aria-hidden="true" className="size-4 shrink-0" />
        <span className="min-w-0 truncate">{workGroupLabel(entries)}</span>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")}
        />
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <div className="space-y-0.5 pt-1">
          {entries.map((entry) => (
            <ActivityRow key={entry.id} workEntry={entry} threadId={threadId} />
          ))}
        </div>
      </CollapsiblePanel>
    </Collapsible>
  );
});

function workToneClass(tone: "thinking" | "tool" | "info" | "error"): string {
  if (tone === "error") return "text-destructive";
  return "text-muted-foreground";
}

function workEntryPreview(workEntry: Pick<WorkLogEntry, "detail" | "command" | "changedFiles">) {
  if (workEntry.command) return workEntry.command;
  if (workEntry.detail) return workEntry.detail;
  if ((workEntry.changedFiles?.length ?? 0) === 0) return null;
  const [firstPath] = workEntry.changedFiles ?? [];
  if (!firstPath) return null;
  return workEntry.changedFiles!.length === 1
    ? firstPath
    : `${firstPath} +${workEntry.changedFiles!.length - 1} more`;
}

function workEntryIcon(workEntry: WorkLogEntry): LucideIcon {
  switch (workEntryCategory(workEntry)) {
    case "command":
      return TerminalIcon;
    case "file-read":
      return BookOpenIcon;
    case "file-search":
      return SearchIcon;
    case "file-list":
      return FolderIcon;
    case "image":
      return EyeIcon;
    case "file-change":
      return SquarePenIcon;
    case "web-search":
      return GlobeIcon;
    case "mcp-tool":
      return WrenchIcon;
    case "tool":
      return HammerIcon;
    case "approval":
      return workEntry.requestKind === "command" ? TerminalIcon : CheckIcon;
    case "error":
      return CircleAlertIcon;
    case "thinking":
      return BotIcon;
    case "other-tool":
      return ZapIcon;
    default:
      return CheckIcon;
  }
}

function capitalizePhrase(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return value;
  }
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}

function toolWorkEntryHeading(workEntry: WorkLogEntry): string {
  if (workEntry.command && workEntry.label === "Command started") return "Started";
  if (workEntry.command && workEntry.label === "Command completed") return "Ran";
  if (!workEntry.toolTitle) {
    return capitalizePhrase(normalizeCompactToolLabel(workEntry.label));
  }
  return capitalizePhrase(normalizeCompactToolLabel(workEntry.toolTitle));
}

const ActivityRow = memo(function ActivityRow(props: {
  workEntry: WorkLogEntry;
  threadId?: ThreadId | undefined;
}) {
  const { workEntry } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const EntryIcon = workEntryIcon(workEntry);
  const heading = toolWorkEntryHeading(workEntry);
  const preview = workEntryPreview(workEntry);
  const displayText =
    dynamicToolLabel(workEntry) ??
    commandActionLabel(workEntry) ??
    (preview ? `${heading} ${preview}` : heading);
  const hasChangedFiles = (workEntry.changedFiles?.length ?? 0) > 0;
  const hasDetails = Boolean(
    (workEntry.payloadDeferred && !workEntry.toolName) ||
    workEntry.command ||
    workEntry.detail ||
    hasChangedFiles,
  );
  const summary = (
    <>
      <EntryIcon aria-hidden="true" className="size-4 shrink-0" />
      <span className="min-w-0 truncate">{displayText}</span>
    </>
  );
  const rowClassName = cn(
    "flex min-w-0 items-center gap-2 py-1 text-left text-sm leading-5",
    workToneClass(workEntry.tone),
  );

  if (!hasDetails) {
    return <div className={rowClassName}>{summary}</div>;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="min-w-0">
      <CollapsibleTrigger
        className={cn(
          rowClassName,
          "max-w-full rounded transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        )}
        title={displayText}
      >
        {summary}
        <ChevronDownIcon
          aria-hidden="true"
          className={cn("size-3.5 shrink-0 transition-transform", isExpanded && "rotate-180")}
        />
      </CollapsibleTrigger>
      <CollapsiblePanel>
        {isExpanded ? (
          workEntry.payloadDeferred && props.threadId && workEntry.activityId ? (
            <DeferredActivityContent
              threadId={props.threadId}
              activityId={workEntry.activityId}
              entryId={workEntry.id}
            />
          ) : (
            <ActivityContent workEntry={workEntry} />
          )
        ) : null}
      </CollapsiblePanel>
    </Collapsible>
  );
});

function DeferredActivityContent({
  threadId,
  activityId,
  entryId,
}: {
  threadId: ThreadId;
  activityId: EventId;
  entryId: string;
}) {
  const query = useQuery(activityDetailQueryOptions(threadId, activityId));
  if (query.isPending)
    return (
      <p role="status" className="py-2 text-xs text-muted-foreground">
        Loading activity details...
      </p>
    );
  if (query.isError)
    return (
      <div role="alert" className="py-2 text-xs text-destructive">
        Could not load activity details.{" "}
        <button type="button" className="underline" onClick={() => void query.refetch()}>
          Retry
        </button>
      </div>
    );
  const entries = deriveWorkLogEntries([query.data], undefined);
  const entry = entries.find((item) => item.id === entryId) ?? entries[0];
  return entry ? <ActivityContent workEntry={entry} /> : null;
}

function ActivityContent({ workEntry }: { workEntry: WorkLogEntry }) {
  const hasChangedFiles = (workEntry.changedFiles?.length ?? 0) > 0;
  return (
    <TextOutput
      title={workEntry.command ? "Shell" : "Details"}
      className="my-2"
      contentClassName="max-h-80"
      {...(workEntry.command ? { copyText: workEntry.command, copyLabel: "Copy command" } : {})}
    >
      {workEntry.command && (
        <pre className="font-mono text-xs leading-6 text-foreground">
          <code>{workEntry.command}</code>
        </pre>
      )}
      {workEntry.detail && workEntry.detail !== workEntry.command && (
        <pre className="mt-2 font-mono text-xs leading-6 text-muted-foreground">
          {workEntry.detail}
        </pre>
      )}
      {hasChangedFiles && (
        <ul className="text-output-lines space-y-1 font-mono text-xs leading-6 text-foreground">
          {workEntry.changedFiles?.map((filePath) => (
            <li key={filePath}>{filePath}</li>
          ))}
        </ul>
      )}
    </TextOutput>
  );
}
